import { useState } from 'react'
import { ListFilter } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import type { BidPriority } from '@/lib/schemas'
import { BID_STAGES, STAGE_LABELS, type BidStage } from '@/lib/bid-lifecycle'

export interface BidsFilters {
  /** R4: stages replace statuses; the lifecycle is the Bids tab. */
  stages: BidStage[]
  priorities: BidPriority[]
  deadlineFrom?: string | undefined
  deadlineTo?: string | undefined
  valueMin?: number | undefined
  valueMax?: number | undefined
}

interface BidsFilterPopoverProps {
  filters: BidsFilters
  onChange: (filters: BidsFilters) => void
  /** Stages only exist on active bids: the other tabs hide the section. */
  showStages?: boolean
}

const PRIORITY_OPTIONS: BidPriority[] = ['low', 'medium', 'high', 'critical']

const PRIORITY_LABELS: Record<BidPriority, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  critical: 'Critical',
}

function getActiveFilterCount(filters: BidsFilters): number {
  let count = 0
  if (filters.stages.length > 0) count++
  if (filters.priorities.length > 0) count++
  if (filters.deadlineFrom || filters.deadlineTo) count++
  if (filters.valueMin !== undefined || filters.valueMax !== undefined) count++
  return count
}

export function BidsFilterPopover({ filters, onChange, showStages = true }: BidsFilterPopoverProps) {
  const [localFilters, setLocalFilters] = useState<BidsFilters>(filters)
  const activeCount = getActiveFilterCount(filters)

  const handleStageToggle = (stage: BidStage) => {
    const newStages = localFilters.stages.includes(stage)
      ? localFilters.stages.filter((s) => s !== stage)
      : [...localFilters.stages, stage]

    const updated = { ...localFilters, stages: newStages }
    setLocalFilters(updated)
    onChange(updated)
  }

  const handlePriorityToggle = (priority: BidPriority) => {
    const newPriorities = localFilters.priorities.includes(priority)
      ? localFilters.priorities.filter((p) => p !== priority)
      : [...localFilters.priorities, priority]

    const updated = { ...localFilters, priorities: newPriorities }
    setLocalFilters(updated)
    onChange(updated)
  }

  const handleDeadlineFromChange = (value: string) => {
    const updated: BidsFilters = { ...localFilters }
    if (value) {
      updated.deadlineFrom = value
    } else {
      delete updated.deadlineFrom
    }
    setLocalFilters(updated)
    onChange(updated)
  }

  const handleDeadlineToChange = (value: string) => {
    const updated: BidsFilters = { ...localFilters }
    if (value) {
      updated.deadlineTo = value
    } else {
      delete updated.deadlineTo
    }
    setLocalFilters(updated)
    onChange(updated)
  }

  const handleValueMinChange = (value: string) => {
    const updated: BidsFilters = { ...localFilters }
    if (value) {
      updated.valueMin = parseFloat(value)
    } else {
      delete updated.valueMin
    }
    setLocalFilters(updated)
    onChange(updated)
  }

  const handleValueMaxChange = (value: string) => {
    const updated: BidsFilters = { ...localFilters }
    if (value) {
      updated.valueMax = parseFloat(value)
    } else {
      delete updated.valueMax
    }
    setLocalFilters(updated)
    onChange(updated)
  }

  const handleClearAll = () => {
    const cleared: BidsFilters = {
      stages: [],
      priorities: [],
    }
    setLocalFilters(cleared)
    onChange(cleared)
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8 relative" title="Filter">
          <ListFilter className="h-4 w-4" />
          {activeCount > 0 && (
            <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-primary text-primary-foreground text-[10px] flex items-center justify-center">
              {activeCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-4 space-y-4" align="start">
        {/* Stage Section */}
        {showStages && (
        <div className="space-y-2">
          <Label className="text-sm font-medium">Stage</Label>
          <div className="space-y-2">
            {BID_STAGES.map((stage) => (
              <div key={stage} className="flex items-center gap-2">
                <Checkbox
                  id={`stage-${stage}`}
                  checked={localFilters.stages.includes(stage)}
                  onCheckedChange={() => handleStageToggle(stage)}
                />
                <label
                  htmlFor={`stage-${stage}`}
                  className="text-sm cursor-pointer flex-1"
                >
                  {STAGE_LABELS[stage]}
                </label>
              </div>
            ))}
          </div>
        </div>
        )}

        {/* Priority Section */}
        <div className="space-y-2">
          <Label className="text-sm font-medium">Priority</Label>
          <div className="space-y-2">
            {PRIORITY_OPTIONS.map((priority) => (
              <div key={priority} className="flex items-center gap-2">
                <Checkbox
                  id={`priority-${priority}`}
                  checked={localFilters.priorities.includes(priority)}
                  onCheckedChange={() => handlePriorityToggle(priority)}
                />
                <label
                  htmlFor={`priority-${priority}`}
                  className="text-sm cursor-pointer flex-1"
                >
                  {PRIORITY_LABELS[priority]}
                </label>
              </div>
            ))}
          </div>
        </div>

        {/* Deadline Range Section */}
        <div className="space-y-2">
          <Label className="text-sm font-medium">Deadline range</Label>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Input
                type="date"
                value={localFilters.deadlineFrom || ''}
                onChange={(e) => handleDeadlineFromChange(e.target.value)}
                placeholder="From"
                className="text-sm"
              />
            </div>
            <div>
              <Input
                type="date"
                value={localFilters.deadlineTo || ''}
                onChange={(e) => handleDeadlineToChange(e.target.value)}
                placeholder="To"
                className="text-sm"
              />
            </div>
          </div>
        </div>

        {/* Value Range Section */}
        <div className="space-y-2">
          <Label className="text-sm font-medium">Value range</Label>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Input
                type="number"
                value={localFilters.valueMin ?? ''}
                onChange={(e) => handleValueMinChange(e.target.value)}
                placeholder="Min"
                min={0}
                className="text-sm"
              />
            </div>
            <div>
              <Input
                type="number"
                value={localFilters.valueMax ?? ''}
                onChange={(e) => handleValueMaxChange(e.target.value)}
                placeholder="Max"
                min={localFilters.valueMin ?? 0}
                className="text-sm"
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="pt-2 border-t">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClearAll}
            className="w-full text-muted-foreground"
          >
            Clear all
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  )
}
